/**
 * Mentality data adapters — map backend wire shapes to the card shape
 * the overlay renders. Pure functions so they're trivially testable.
 *
 * Phase E4: extracted from MentalityOverlay. Re-exported from
 * `../MentalityOverlay.tsx` so existing test paths keep working.
 */

import type {
    Risk, Insight, ClientProfile, MentalityContext,
} from '../../../mentorshipService';

export type MatchCard = {
    kind: 'risk' | 'insight' | 'profile';
    title: string;
    then: string;
    meta?: string;
};

export function riskToCard(r: Risk): MatchCard {
    return {
        kind: 'risk',
        title: r.trigger,
        then: r.mitigation || r.impact || 'Flag with your mentor.',
        meta: r.priority,
    };
}

export function insightToCard(i: Insight): MatchCard {
    return {
        kind: 'insight',
        title: i.trigger_context || 'General prompt',
        then: i.prompt,
        meta: i.priority,
    };
}

export function profileToCards(p: ClientProfile): MatchCard[] {
    const cards: MatchCard[] = [];
    for (const advice of p.management_advice.slice(0, 2)) {
        cards.push({
            kind: 'profile',
            title: `Client ${p.client_id || '—'}`,
            then: advice,
            meta: 'client',
        });
    }
    return cards;
}

/** Resolve LLM-returned rule refs back into MatchCards so chat bubbles
 *  render the same way as the top-rules list. Silently drops IDs the
 *  LLM hallucinated that don't exist in the current context. */
export function resolveRuleRefs(
    refs: { id: string; kind: string }[],
    context: MentalityContext,
): MatchCard[] {
    const out: MatchCard[] = [];
    for (const ref of refs) {
        if (ref.kind === 'risk') {
            const r = context.risks.find((x) => x.id === ref.id);
            if (r) out.push(riskToCard(r));
        } else if (ref.kind === 'insight') {
            const i = context.insights.find((x) => x.id === ref.id);
            if (i) out.push(insightToCard(i));
        } else if (ref.kind === 'profile') {
            const p = context.client_profiles.find((x) => x.id === ref.id);
            if (p) out.push(...profileToCards(p));
        }
    }
    return out;
}
